import ensureError from "@-/com.npmjs.ensure-error";
import * as z from "@-/com.npmjs.zod";

/**
 * Why a local backup failed, precisely. Creating a backup only copies files on this device, so
 * the only failures are the device refusing to store the copy or a missing native engine; every
 * other kind is a read-time (restore/export) problem or a bug, and says so.
 */
export type BackupFailureKind =
  /** No space on the volume. Carries free and needed bytes when the platform reports them. */
  | "storage-full"
  /** There is no study database file to copy (it is being reset, or was never created). */
  | "source-missing"
  /** The study database restarted its write-ahead log during every copy attempt. */
  | "source-unstable"
  /** The requested backup does not exist. */
  | "not-found"
  /** A saved backup cannot be read back (restore/export only). */
  | "unreadable"
  /** Any other operating-system I/O error, with its errno. */
  | "io"
  /** Android: the separate process that copies the live files was stopped mid-copy. */
  | "helper-stopped"
  /** This binary has no file backup engine (an OTA update ahead of a native rebuild). */
  | "native-unavailable"
  /** A step ran past its bound. The stage names it. */
  | "timeout"
  /** A malformed request between JavaScript and native code: a bug. */
  | "invalid-request"
  /** Not a backup failure we classified: carries the original error's name and message. */
  | "unexpected";

const NATIVE_FAILURE_MARKER = "HANIVERSE_BACKUP_FAILURE";
const SENTENCE_END = /[.!?)]$/u;

const nativeFailureSchema = z.object({
  availableBytes: z.number().optional(),
  code: z.number().optional(),
  detail: z.string(),
  kind: z.enum([
    "storage-full",
    "source-missing",
    "source-unstable",
    "not-found",
    "unreadable",
    "io",
    "helper-stopped",
    "invalid-request",
  ]),
  requiredBytes: z.number().optional(),
  stage: z.string(),
});

export interface BackupFailureDetails {
  availableBytes?: number;
  /** errno or SQLite result code, when the failing call reported one. */
  code?: number;
  /** The failing call and its reason, e.g. `clonefile SQLite/actor.db-wal failed: No space left on device`. */
  detail: string;
  kind: BackupFailureKind;
  requiredBytes?: number;
  /** prepare | capture | finish | publish | list | read | remove | prune | … */
  stage: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.max(0, Math.round(bytes / 1024))} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function sentence(text: string): string {
  const trimmed = text.trim();
  return SENTENCE_END.test(trimmed) ? trimmed : `${trimmed}.`;
}

/** One self-contained sentence a person can act on, ending with the technical cause. */
export function describeBackupFailure(failure: BackupFailureDetails): string {
  switch (failure.kind) {
    case "storage-full": {
      const space = [
        failure.availableBytes === undefined ? null : `${formatBytes(failure.availableBytes)} free`,
        failure.requiredBytes === undefined || failure.requiredBytes === 0
          ? null
          : `about ${formatBytes(failure.requiredBytes)} needed`,
      ]
        .filter((part) => part !== null)
        .join(", ");
      return `This device is out of storage${space === "" ? "" : ` (${space})`}. Free up space and try again. [${failure.stage}: ${failure.detail}]`;
    }
    case "source-missing": {
      return sentence(failure.detail);
    }
    case "source-unstable": {
      return `The study database kept changing while it was being copied. Try again in a moment. [${failure.detail}]`;
    }
    case "not-found": {
      return sentence(failure.detail);
    }
    case "unreadable": {
      return `This backup can't be read back: ${sentence(failure.detail)}`;
    }
    case "io": {
      return `The device refused a file operation while ${failure.stage === "read" ? "reading" : "saving"} the backup: ${sentence(failure.detail)}`;
    }
    case "helper-stopped": {
      return sentence(failure.detail);
    }
    case "native-unavailable": {
      return "This build of Haniverse has no backup engine. Update the app to save backups.";
    }
    case "timeout": {
      return sentence(failure.detail);
    }
    case "invalid-request": {
      return `Internal backup error (${failure.stage}): ${sentence(failure.detail)}`;
    }
    case "unexpected": {
      return `Unexpected backup error during ${failure.stage}: ${sentence(failure.detail)}`;
    }
  }
}

export class BackupFailure extends Error implements BackupFailureDetails {
  override readonly name = "BackupFailure";
  readonly availableBytes?: number;
  readonly code?: number;
  readonly detail: string;
  readonly kind: BackupFailureKind;
  readonly requiredBytes?: number;
  readonly stage: string;

  constructor(details: BackupFailureDetails, options?: { cause?: unknown }) {
    super(describeBackupFailure(details), options);
    this.kind = details.kind;
    this.stage = details.stage;
    this.detail = details.detail;
    if (details.code !== undefined) {
      this.code = details.code;
    }
    if (details.availableBytes !== undefined) {
      this.availableBytes = details.availableBytes;
    }
    if (details.requiredBytes !== undefined) {
      this.requiredBytes = details.requiredBytes;
    }
  }
}

/**
 * The precise failure inside a native rejection (the engine's `HANIVERSE_BACKUP_FAILURE{…}`
 * payload, wherever Expo's wrapping put it), or the error itself as an `unexpected` failure that
 * keeps its original name and message.
 */
export function toBackupFailure(error: unknown, stage: string): BackupFailure {
  if (error instanceof BackupFailure) {
    return error;
  }
  const original = ensureError(error);
  const text = `${original.message}\n${String(Reflect.get(original, "cause") ?? "")}`;
  const start = text.indexOf(NATIVE_FAILURE_MARKER);
  if (start !== -1) {
    const json = extractJsonObject(text.slice(start + NATIVE_FAILURE_MARKER.length));
    const parsed = json === null ? null : nativeFailureSchema.safeParse(safeJsonParse(json));
    if (parsed?.success === true) {
      return new BackupFailure(parsed.data, { cause: original });
    }
  }
  return new BackupFailure(
    { detail: `${original.name}: ${original.message}`, kind: "unexpected", stage },
    { cause: original },
  );
}

/** The leading balanced `{…}` (string-aware), so trailing wrapper text never breaks parsing. */
function extractJsonObject(text: string): string | null {
  if (!text.startsWith("{")) {
    return null;
  }
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (character === "\\") {
        escaped = true;
      } else if (character === '"') {
        inString = false;
      }
      continue;
    }
    switch (character) {
    case '"': {
      inString = true;
    
    break;
    }
    case "{": {
      depth += 1;
    
    break;
    }
    case "}": {
      depth -= 1;
      if (depth === 0) {
        return text.slice(0, index + 1);
      }
    
    break;
    }
    // No default
    }
  }
  return null;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
